import React, { useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import BottomSheetDialog from '../../../components/BottomSheetDialog';
import FullScreenDialog from '../../../components/FullScreenDialog';
import {
  useCreateLeadList,
  useLeadListsQuery,
  useLeadPickerQuery,
  useUpdateLeadList,
} from '../hooks/useLeadsQuery';
import LeadsDeleteListDialog from './Leads.DeleteListDialog';
import LeadsLeadPickerDialog from './Leads.LeadPickerDialog';
import { AddAvatar, LeadListItem, LoadMoreSentinel, leadName } from './Leads.ListLeadItems';

const NAME_MAX_LENGTH = 60;

// Linha "Adicionar leads" no topo de "Na lista", como no WhatsApp.
const AddLeadsRow = ({ onClick }) => {
  const { t } = useTranslation();
  return (
    <ListItem disablePadding>
      <ListItemButton onClick={onClick} sx={{ px: 0 }}>
        <ListItemAvatar>
          <AddAvatar><AddIcon /></AddAvatar>
        </ListItemAvatar>
        <ListItemText primary={t('leads.lists.addLeads')} />
      </ListItemButton>
    </ListItem>
  );
};

const RemoveLeadButton = ({ lead, onClick }) => {
  const { t } = useTranslation();
  const label = t('leads.lists.removeLead', { name: leadName(lead) });
  return (
    <Tooltip title={label}>
      <IconButton edge="end" aria-label={label} onClick={onClick}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const subheader = (label) => (
  <ListSubheader disableGutters sx={{ bgcolor: 'transparent', position: 'static' }}>{label}</ListSubheader>
);

// Lista existente, como no WhatsApp: título com o nome, excluir e editar no
// cabeçalho, e "Na lista" com "Adicionar leads" + os membros paginados.
// Adicionar vale na hora; remover e renomear ficam na tela "Editar" (lápis).
const ViewListDialog = ({ list: initialList, onClose }) => {
  const { t } = useTranslation();
  const { data: lists = [] } = useLeadListsQuery();
  // Lista atualizada do cache (nome e membros mudam enquanto a tela está aberta).
  const list = lists.find((item) => item.uuid === initialList.uuid) ?? initialList;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const members = useLeadPickerQuery({ listUuid: list.uuid });
  const leads = useMemo(() => members.data?.pages.flatMap((page) => page.results) ?? [], [members.data]);
  const updateMutation = useUpdateLeadList();

  return (
    <FullScreenDialog
      open
      onClose={onClose}
      title={list.name}
      maxWidth="sm"
      headerActions={(
        <>
          <Tooltip title={t('leads.lists.delete')}>
            <IconButton aria-label={t('leads.lists.delete')} onClick={() => setConfirmDelete(true)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('leads.lists.editList')}>
            <IconButton edge="end" aria-label={t('leads.lists.editList')} onClick={() => setEditing(true)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
        </>
      )}
    >
      <List disablePadding subheader={subheader(t('leads.lists.inList'))}>
        <AddLeadsRow onClick={() => setPickerOpen(true)} />
        {members.isLoading ? (
          <Stack alignItems="center" py={3}>
            <CircularProgress size={24} />
          </Stack>
        ) : leads.map((lead) => <LeadListItem key={lead.uuid} lead={lead} />)}
        <LoadMoreSentinel
          hasNextPage={members.hasNextPage}
          isFetchingNextPage={members.isFetchingNextPage}
          onLoadMore={members.fetchNextPage}
        />
      </List>

      {pickerOpen && (
        <LeadsLeadPickerDialog
          open
          initialSelected={list.leads ?? []}
          isSaving={updateMutation.isPending}
          onClose={() => setPickerOpen(false)}
          onConfirm={(uuids) =>
            updateMutation.mutate({ uuid: list.uuid, leads: uuids }, { onSuccess: () => setPickerOpen(false) })}
        />
      )}
      {editing && <EditListFormDialog list={list} onClose={() => setEditing(false)} />}
      {confirmDelete && (
        <LeadsDeleteListDialog list={list} onClose={() => setConfirmDelete(false)} onDeleted={onClose} />
      )}
    </FullScreenDialog>
  );
};

// Folha "Editar" (lápis), como no WhatsApp: X cancela, ✓ salva. Nome e membros
// (adicionar/remover com a lixeira) só são gravados no ✓, num único PATCH.
const EditListFormDialog = ({ list, onClose }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(list.name);
  const [memberUuids, setMemberUuids] = useState(() => new Set(list.leads ?? []));
  const [known, setKnown] = useState({}); // leads adicionados aqui, para exibir
  const [pickerOpen, setPickerOpen] = useState(false);
  const updateMutation = useUpdateLeadList();

  const members = useLeadPickerQuery({ listUuid: list.uuid });
  const original = useMemo(() => new Set(list.leads ?? []), [list.leads]);
  // Adicionados nesta edição vêm primeiro; depois os que já estavam e não foram removidos.
  const added = [...memberUuids].filter((uuid) => !original.has(uuid) && known[uuid]).map((uuid) => known[uuid]);
  const kept = (members.data?.pages.flatMap((page) => page.results) ?? []).filter((lead) => memberUuids.has(lead.uuid));

  const remove = (uuid) => setMemberUuids((prev) => {
    const next = new Set(prev);
    next.delete(uuid);
    return next;
  });

  const save = () =>
    updateMutation.mutate(
      { uuid: list.uuid, name: name.trim(), leads: [...memberUuids] },
      { onSuccess: onClose },
    );

  return (
    <BottomSheetDialog
      open
      onClose={onClose}
      title={t('leads.lists.editTitle')}
      action={(
        <Tooltip title={t('leads.save')}>
          <span>
            <IconButton
              edge="end"
              color="primary"
              aria-label={t('leads.save')}
              disabled={!name.trim() || updateMutation.isPending}
              onClick={save}
            >
              <CheckIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
    >
      <TextField
        autoFocus
        fullWidth
        size="small"
        label={t('leads.lists.name')}
        value={name}
        onChange={(e) => setName(e.target.value)}
        slotProps={{ htmlInput: { maxLength: NAME_MAX_LENGTH } }}
        sx={{ mt: 1, mb: 2 }}
      />
      <List disablePadding subheader={subheader(t('leads.lists.inList'))}>
        <AddLeadsRow onClick={() => setPickerOpen(true)} />
        {[...added, ...kept].map((lead) => (
          <LeadListItem
            key={lead.uuid}
            lead={lead}
            action={<RemoveLeadButton lead={lead} onClick={() => remove(lead.uuid)} />}
          />
        ))}
        <LoadMoreSentinel
          hasNextPage={members.hasNextPage}
          isFetchingNextPage={members.isFetchingNextPage}
          onLoadMore={members.fetchNextPage}
        />
      </List>

      {pickerOpen && (
        <LeadsLeadPickerDialog
          open
          initialSelected={[...memberUuids]}
          onClose={() => setPickerOpen(false)}
          onConfirm={(uuids, leadsByUuid) => {
            setKnown((prev) => ({ ...prev, ...leadsByUuid }));
            setMemberUuids(new Set(uuids));
            setPickerOpen(false);
          }}
        />
      )}
    </BottomSheetDialog>
  );
};

// Lista nova: nome + "Adicionar leads"; só é criada ao salvar.
const CreateListDialog = ({ onClose, onCreated }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [selected, setSelected] = useState([]); // uuids
  const [known, setKnown] = useState({}); // uuid → lead, para exibir os escolhidos
  const [pickerOpen, setPickerOpen] = useState(false);
  const createMutation = useCreateLeadList();

  const handleSave = () =>
    createMutation.mutate({ name: name.trim(), leads: selected }, { onSuccess: onCreated });

  return (
    <FullScreenDialog
      open
      onClose={onClose}
      title={t('leads.lists.create')}
      maxWidth="sm"
      actions={(
        <Stack direction="row" spacing={1.5} sx={{ width: '100%' }}>
          <Button fullWidth variant="outlined" size="large" onClick={onClose}>
            {t('leads.cancel')}
          </Button>
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<CheckIcon />}
            disabled={!name.trim() || createMutation.isPending}
            onClick={handleSave}
          >
            {t('leads.save')}
          </Button>
        </Stack>
      )}
    >
      <TextField
        autoFocus
        fullWidth
        size="small"
        label={t('leads.lists.name')}
        value={name}
        onChange={(e) => setName(e.target.value)}
        slotProps={{ htmlInput: { maxLength: NAME_MAX_LENGTH } }}
        sx={{ mb: 2 }}
      />
      <List disablePadding subheader={subheader(t('leads.lists.inList'))}>
        <AddLeadsRow onClick={() => setPickerOpen(true)} />
        {selected.map((uuid) => known[uuid] && (
          <LeadListItem
            key={uuid}
            lead={known[uuid]}
            action={(
              <RemoveLeadButton
                lead={known[uuid]}
                onClick={() => setSelected((prev) => prev.filter((item) => item !== uuid))}
              />
            )}
          />
        ))}
      </List>
      {selected.length === 0 && (
        <Typography variant="body2" color="text.secondary">{t('leads.lists.createHint')}</Typography>
      )}

      {pickerOpen && (
        <LeadsLeadPickerDialog
          open
          initialSelected={selected}
          onClose={() => setPickerOpen(false)}
          onConfirm={(uuids, leadsByUuid) => {
            setKnown((prev) => ({ ...prev, ...leadsByUuid }));
            setSelected(uuids);
            setPickerOpen(false);
          }}
        />
      )}
    </FullScreenDialog>
  );
};

// Cria (list sem uuid) ou gerencia uma lista. Montado só quando aberto.
const LeadsListDialog = ({ list, onClose, onCreated }) =>
  (list.uuid
    ? <ViewListDialog list={list} onClose={onClose} />
    : <CreateListDialog onClose={onClose} onCreated={onCreated} />);

export default LeadsListDialog;
